import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DataTable from "react-data-table-component";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { createColumns } from "../../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../../constants/datatableStyles";
import CustomPagination from "../../../Components/CustomPagination";
import {
  getPartnerTransactionsHistory,
  getTransactionsCategory,
  getWalletTypes,
} from "../../../apis";
import PageTitle from "../../../Components/UI/PageTitle";
import DropdownMultiSelect from "../../../Components/UI/DropdownMultiSelect";
import EyeDetailsIcon from "../../../Components/Svgs/EyeDetailsIcon";
import RowLoader from "../../../Components/UI/RowLoader";
import { transformKeys } from "../../../_helper";
import ErrorAnimationComponent from "../../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import { useSelector } from "react-redux";
import { USER_TYPES } from "../../../constants/globals";

const PartnerTransactionsHistory = () => {
  const { id } = useParams();
  const { loggedInUser } = useSelector((state) => state.Auth);
  const currentUserType = loggedInUser?.data?.model?.user_type_id;
  const endpointUrl =
    currentUserType === USER_TYPES.ADMIN ||
    currentUserType === USER_TYPES.MODERATOR
      ? "adminTransactionHistory"
      : "clientTransactionHistory";

  const location = useLocation();
  const navigate = useNavigate();

  // Extract URL search parameters
  const searchParams = new URLSearchParams(location.search);
  const initialSearchTerm = searchParams.get("search") || "";
  const initialPage = parseInt(searchParams.get("page"), 10) || 1;

  // State variables
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [requestsData, setRequestsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setExportLoading] = useState(false);
  const [isError, setIsError] = useState("");
  const [pagination, setPagination] = useState({});
  const [transactionCategory, setTransactionCategory] = useState([]);
  const [walletTypes, setWalletTypes] = useState([]);
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );
  const [selectedWalletTypes, setSelectedWalletTypes] = useState([]);
  const [selectedTransCategoires, setSelectedTransCategoire] = useState([]);

  const handleSelectedTransCat = selectedTransCategoires
    .map((trans) => trans.value)
    .join(",");
  const handleSelectedWalletTypes = selectedWalletTypes
    .map((type) => type.value)
    .join(",");

  useEffect(() => {
    setIsLoading(true);
    getPartnerTransactionsHistory(
      id,
      handleSelectedTransCat,
      currentPage,
      searchTerm,
      sortKey,
      sortDirection,
      startDate,
      endDate,
      handleSelectedWalletTypes
    )
      .then((response) => {
        setPagination(response.data.paginator);
        const handleData = response.data.data.map((d) => transformKeys(d));
        setRequestsData(handleData);
      })
      .catch((error) => {
        setIsError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    id,
    currentPage,
    searchTerm,
    sortKey,
    sortDirection,
    startDate,
    endDate,
    handleSelectedTransCat,
    handleSelectedWalletTypes,
  ]);

  // get dropdown data
  useEffect(() => {
    getTransactionsCategory(endpointUrl)
      .then((response) => {
        const sortedArray = response.data.data.sort((a, b) =>
          a.transactionCategoryName.localeCompare(b.transactionCategoryName)
        );
        setTransactionCategory(sortedArray);
      })
      .catch((error) => {
        console.log(error);
      });
    getWalletTypes(endpointUrl)
      .then((response) => {
        setWalletTypes(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [endpointUrl]);

  // Update URL parameters when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() }, { replace: true });
  }, [
    searchTerm,
    startDate,
    endDate,
    currentPage,
    navigate,
    sortDirection,
    sortKey,
  ]);

  // Handlers
  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { data } = await axios.get(
        `/admin/partners-list/profile/transactionHistory/excelExport/${id}`,
        {
          params: {
            user_id: id,
            search: searchTerm,
            sort_by: sortKey,
            sort_dir: sortDirection,
            fromDate: startDate,
            toDate: endDate,
            WalletType: handleSelectedWalletTypes,
            TransCat: handleSelectedTransCat,
          },
          responseType: "blob",
        }
      );

      const fileName = `Transaction_History_For_${
        requestsData?.[0]?.transaction_for_client?.split(" ")[0] ?? ""
      }_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const hiddenColumns =
    currentUserType === USER_TYPES.ADMIN ||
    currentUserType === USER_TYPES.MODERATOR
      ? ["transaction_id"]
      : ["transaction_id", "transaction_for_client"]; // Add column keys you want to hide

  const customColumns = [
    ...[
      {
        name: "Details",
        selector: (row) => row.name + " - custom",
        ui: (row) => (
          <div className="tw-flex tw-gap-1">
            <Link to={`/transactions/${row.transaction_id}`}>
              <EyeDetailsIcon width="30px" height="30px" />
            </Link>
          </div>
        ),
      },
    ],
  ];

  const filterAndSortData = () => {
    return requestsData;
  };

  const handleSortChange = (column, direction) => {
    setSortDirection(direction);
    setSortKey(column.sortKey);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const columns = useMemo(() => {
    return createColumns(requestsData, customColumns, hiddenColumns, true);
  }, [requestsData]).map((col) => ({
    ...col,
    // sortable: [
    //   "Client Wallet Type",
    //   "Amount",
    //   "Transaction Category",
    //   "Transaction Date",
    // ].includes(col.name),
    sortable: false,
    width:
      col.name === "Num" || col.name === "Details"
        ? "80px"
        : col.name === "Name"
        ? "300px"
        : "1fr",
    cell:
      col.name === "Amount"
        ? (row) => {
            const amount = parseFloat(row.amount.replace(/[^\d.-]/g, "")) || 0;
            const isNegative = amount < 0;

            return (
              <span
                className={isNegative ? "tw-text-red-500" : "tw-text-green-500"}
              >
                {row.amount} EGP
              </span>
            );
          }
        : col.cell,
  }));

  if (isError) return <ErrorAnimationComponent message={isError} />;

  return (
    <>
      <PageTitle
        title={`Transactions History For ${
          requestsData?.[0]?.transaction_for_client ?? ""
        }`}
      />

      <div className="bg-white flex flex-col">
        <div className="bg-white shadow-lg rounded-lg p-3 gap-2 mb-5">
          <input
            type="text"
            onChange={handleFilterChange}
            value={searchTerm}
            className="border border-blue-300 rounded-lg p-2 w-full"
            placeholder="Search by name, email, phone"
          />

          <div className="tw-mb-2 tw-grid xl:tw-grid-cols-4 tw-gap-4 lg:tw-grid-cols-2 tw-grid-cols-1 tw-items-end tw-mt-2">
            <DropdownMultiSelect
              options={transactionCategory ?? []}
              placeholder="Transaction Category"
              valueKey="transactionCategoryId"
              labelKey="transactionCategoryName"
              setSelectedItems={setSelectedTransCategoire}
              setCurrentPage={setCurrentPage}
            />
            <DropdownMultiSelect
              options={walletTypes}
              placeholder="Wallet Type"
              valueKey="WalletTypeId"
              labelKey="WalletTypeName"
              setSelectedItems={setSelectedWalletTypes}
              setCurrentPage={setCurrentPage}
            />
            <div className="tw-flex tw-items-center tw-relative">
              <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28 tw-pe-2">
                Date From:
              </span>
              <Flatpickr
                value={startDate}
                onChange={([date]) => setStartDate(date)}
                className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
              />
              {startDate && (
                <button
                  onClick={() => setStartDate(null)}
                  className="tw-p-2 tw-text-[#797777] tw-absolute -tw-end-2 tw-text-lg tw-font-extrabold"
                >
                  x
                </button>
              )}
            </div>
            <div className="tw-flex tw-items-center tw-relative">
              <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28">
                Date To:
              </span>
              <Flatpickr
                value={endDate}
                onChange={([date]) => setEndDate(date)}
                className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
              />
              {endDate && (
                <button
                  onClick={() => setEndDate(null)}
                  className="tw-p-2 tw-text-[#797777] tw-absolute -tw-end-2 tw-text-lg tw-font-extrabold"
                >
                  x
                </button>
              )}
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filterAndSortData()}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            progressComponent={<RowLoader />}
            onSort={handleSortChange}
            sortServer
          />

          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={pagination?.last_page}
              currentPage={pagination?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      {requestsData?.length > 0 && (
        <button
          className={`tw-border tw-bg-green-600/20 tw-flex tw-justify-center tw-w-[200px] tw-h-10 tw-text-center tw-border-green-500 tw-px-4 tw-py-2 tw-rounded-lg tw-items-center tw-gap-2 tw-transition-all hover:tw-bg-green-600/30`}
          onClick={handleExport}
        >
          {isExportLoading ? (
            <div className="tw-flex tw-justify-center">
              <RowLoader paddingStyle="tw-py-0" />
            </div>
          ) : (
            <>
              <img
                src="/imgs/excelIcon.png"
                className="tw-size-5"
                alt="Export"
              />
              <span>Export as excel file</span>
            </>
          )}
        </button>
      )}
    </>
  );
};

export default PartnerTransactionsHistory;
